
// Function to determine the probability class based on score
export const getProbabilityClass = (score) => {
    if (score > 20) return 'H';
    if (score >= 17 && score <= 20) return 'M';
    if (score >= 11 && score <= 16) return 'L';
    if (score >= 1 && score <= 10) return 'N';
    return '';
};

// Function to check if cell should be highlighted based on score and class
export const getHighlightClass = (classKey, score) => {
    const currentClass = getProbabilityClass(score);
    return currentClass === classKey ? 'highlighted-cell' : '';
};

export const shouldHighlightCriticality = (probabilityClass, consequenceIndex, equipmentData) => {
    const currentProbClass = getProbabilityClass(equipmentData?.cumulativeScore);
    const currentConsequence = equipmentData?.consequence;
    const consequenceMap = { 'N': 0, 'L': 1, 'M': 2, 'H': 3, 'E': 4 };

    return probabilityClass === currentProbClass &&
        consequenceIndex === consequenceMap[currentConsequence];
};


export const getMinMaxFromList = (list) => {
    let sortedList = [...list].sort((a, b) => a - b);
    return {
        min: sortedList[0],
        max: sortedList[sortedList.length - 1]
    };
}

export const sortListInOrder = (data, columnId, desc) => {
    // console.log("Sorting data for column:", columnId, "Descending:", desc);
    // console.log("Data before sorting:", data);
    let sortedData = [...data].sort((a, b) => {
        const valueA = a[columnId];
        const valueB = b[columnId];
        // value is always an array, map each value to its order and then sort based on desc flag
        const {min: minA, max: maxA} = getMinMaxFromList(valueA);
        const {min: minB, max: maxB} = getMinMaxFromList(valueB);
        const formattedValueA = desc ? maxA : minA;
        const formattedValueB = desc ? maxB : minB;
        // console.log("Sorting values:", valueA, valueB, "Formatted values:", formattedValueA, formattedValueB);
        if (formattedValueA == null && formattedValueB == null) return 0;
        if (formattedValueA == null) return 1;
        if (formattedValueB == null) return -1;
        if (formattedValueA > formattedValueB) return desc ? -1 : 1;
        if (formattedValueA < formattedValueB) return desc ? 1 : -1;
        return 0;
    });
    // console.log("Data after sorting:", sortedData);
    return sortedData;
};

export const getCriticalityOrder = (value) => {
    const orderMap = {
        'IS-1(E)': 6,
        'IS-2(H)': 5,
        'IS-3(MH)': 4,
        'IS-4(M)': 3,
        'IS-4(L)': 2,
        'IS-4(N)': 1
    };

    return orderMap[value] || 0; // Default high value for unknown criticality
};

export const criticalitySortingFn = (data, columnId, desc) => {
    return [...data].sort((a, b) => {
        const valueA = a[columnId];
        const valueB = b[columnId];
        // value is always an array, map each value to its order and then sort based on desc flag
        const formattedValueA = desc ? Math.max(...valueA.map(getCriticalityOrder)) : Math.min(...valueA.map(getCriticalityOrder));
        const formattedValueB = desc ? Math.max(...valueB.map(getCriticalityOrder)) : Math.min(...valueB.map(getCriticalityOrder));
        // console.log("Sorting values:", valueA, valueB, "Formatted values:", formattedValueA, formattedValueB);
        if (formattedValueA == null && formattedValueB == null) return 0;
        if (formattedValueA == null) return 1;
        if (formattedValueB == null) return -1;
        if (formattedValueA > formattedValueB) return desc ? -1 : 1;
        if (formattedValueA < formattedValueB) return desc ? 1 : -1;
        return 0;
    });
};

export const getConsequenceProbOrder = (value) => {
    const orderMap = {
        'H': 4,
        'M': 3,
        'L': 2,
        'N': 1
    };

    return orderMap[value] || 999; // Default high value for unknown consequence
};

export const consequenceProbSortingFn = (data, columnId, desc) => {
    return [...data].sort((a, b) => {
        const valueA = a[columnId];
        const valueB = b[columnId];
        // value is always an array, map each value to its order and then sort based on desc flag
        const formattedValueA = desc ? Math.max(...valueA.map(getConsequenceProbOrder)) : Math.min(...valueA.map(getConsequenceProbOrder));
        const formattedValueB = desc ? Math.max(...valueB.map(getConsequenceProbOrder)) : Math.min(...valueB.map(getConsequenceProbOrder));
        // console.log("Sorting values:", valueA, valueB, "Formatted values:", formattedValueA, formattedValueB);
        if (formattedValueA == null && formattedValueB == null) return 0;
        if (formattedValueA == null) return 1;
        if (formattedValueB == null) return -1;
        if (formattedValueA > formattedValueB) return desc ? -1 : 1;
        if (formattedValueA < formattedValueB) return desc ? 1 : -1;
        return 0;
    });
};


export const getSummaryFromData = (data) => {
    // count of all susceptibilityStatus values in the data
    const susceptibilityStatusSummary = {
        'category': ['CONDENSATE RISK', 'GREEN', 'AMBER'],
        'count': [0, 0, 0],
    };
    const staticDynamicSummary = {
        'category': ['Static', 'Real-Time'],
        'count': [0, 0]
    };
    const condensateRiskAssessmentSummary = {
        'category': ['Pending', 'Resolved'],
        'count': [0, 0],
    };

    const condensateRiskStatsSummary = {
        "condensateRiskDetectedCount": 0,
        "condensateRiskAcceptedCount": 0,
        "coatingConditionAcceptableCount": 0,
    };
    const countSummary = {
        "units": [],
        "unitCount": 0,
        "equipmentCount": 0,
        "zoneCount": 0,
    };
    [...data].forEach(item => {
        const statuses = item.susceptibilityStatus;
        const temperatureMethods = item.temperatureMethod;
        const condensateRiskAssementStatus = item.condensateRiskAssementStatus;
        if (condensateRiskAssementStatus) {
            condensateRiskAssementStatus.forEach((status) => {
                const index = condensateRiskAssessmentSummary['category'].indexOf(status);
                if (index !== -1) {
                    condensateRiskAssessmentSummary['count'][index] = (condensateRiskAssessmentSummary['count'][index] || 0) + 1;
                }
            });
        }

        if (temperatureMethods) {
            temperatureMethods.forEach(method => {
                const index = staticDynamicSummary['category'].indexOf(method);
                if (index !== -1) {
                    staticDynamicSummary['count'][index] = (staticDynamicSummary['count'][index] || 0) + 1;
                }
            });
        }
        if (statuses) {
            statuses.forEach(status => {
                const index = susceptibilityStatusSummary['category'].indexOf(status);
                if (index !== -1) {
                    susceptibilityStatusSummary['count'][index] = (susceptibilityStatusSummary['count'][index] || 0) + 1;
                }
            });
        }

        countSummary["units"].push(item?.unit?.name || "");
        countSummary["equipmentCount"] = (countSummary["equipmentCount"] || 0) + 1;
        countSummary["zoneCount"] = (countSummary["zoneCount"] || 0) + (item?.zones?.length || 0);

        condensateRiskStatsSummary["condensateRiskDetectedCount"] = (condensateRiskStatsSummary["condensateRiskDetectedCount"] || 0) + (item?.condensateRiskDetected?.filter(val => val === 'True').length || 0);
        condensateRiskStatsSummary["condensateRiskAcceptedCount"] = (condensateRiskStatsSummary["condensateRiskAcceptedCount"] || 0) + (item?.condensateRiskAccepted?.filter(val => val === '1').length || 0);
        condensateRiskStatsSummary["coatingConditionAcceptableCount"] = (condensateRiskStatsSummary["coatingConditionAcceptableCount"] || 0) + (item?.coatingPerformanceAcceptable?.filter(val => val === '1').length || 0);
    });

    countSummary["units"] = [...new Set(countSummary["units"])];
    countSummary["unitCount"] = countSummary["units"].length;
    return { susceptibilityStatusSummary, staticDynamicSummary, condensateRiskAssessmentSummary, countSummary, condensateRiskStatsSummary };
}


